import React, { useCallback, useEffect, useState, useRef } from 'react'
import classNames from 'classnames'
import { shape, func, number, string } from 'prop-types'
import { useTranslation } from 'react-i18next'

import { convertRadToDeg, convertDegToRad } from 'utils/convertRotation'

import FavoriteButtonContainer from 'containers/uiElements/FavoriteButtonContainer'

import Tooltip from 'components/Tooltip'
import Icon from 'components/uiElements/Icon'
import Range from 'components/formElements/Range'
import { TRACK_LOCATION_ACTION_PANEL } from 'models/tracking/constants'

import { mapHeading, generatePositionStyle } from './helpers'

require('./style.scss')

const MODE_ROTATE = 'MODE_ROTATE'
const MODE_MORE = 'MODE_MORE'

const ActionPanel = ({
    targetRef,
    onDelete,
    onRotateStart,
    onRotate,
    onRotateFinish,
    heading,
    onAddToCart,
    onDuplicate,
    onChangeSurface,
    localItemNumber,
}) => {
    const { t } = useTranslation()
    const [position, setPosition] = useState(null)
    // Locally we'll use degrees since that's what we expose to users
    const [localHeading, setLocalHeading] = useState(mapHeading(Math.round(convertRadToDeg(heading))))
    const [isRotating, setIsRotating] = useState(false)
    const [openUp, setOpenUp] = useState(false)
    const [openLeft, setOpenLeft] = useState(false)
    const [mode, setMode] = useState(null)
    const panelRef = useRef()
    const rotateRangeRef = useRef(null)

    const rotateStart = useCallback(() => {
        setIsRotating(true)
        onRotateStart()
    }, [onRotateStart])

    const rotate = useCallback(
        (h) => {
            setLocalHeading(h)
            // Convert to radians for the renderer/data model
            onRotate(convertDegToRad(h))
        },
        [onRotate],
    )

    const rotateFinish = useCallback(() => {
        if (isRotating) {
            let { heading: nextHeading } = onRotateFinish()
            nextHeading = mapHeading(Math.round(convertRadToDeg(nextHeading)))
            setLocalHeading(nextHeading)
            setIsRotating(false)
        }
    }, [localHeading, isRotating])

    const reposition = useCallback(() => {
        if (targetRef && targetRef.current && panelRef.current) {
            const targetRect = targetRef.current.getBoundingClientRect()
            const panelRect = panelRef.current.getBoundingClientRect()
            const contentRect = panelRef.current.parentNode.getBoundingClientRect()
            const docHeight = document.documentElement.clientHeight
            const docWidth = document.documentElement.clientWidth

            const style = generatePositionStyle(targetRect, panelRect, contentRect, docHeight, docWidth)

            if (style.right) {
                setOpenLeft(true)
            } else {
                setOpenLeft(false)
            }

            if (style.bottom) {
                setOpenUp(true)
            } else {
                setOpenUp(false)
            }

            setPosition(style)
        } else {
            setMode(null)
            setPosition(null)
        }
    }, [targetRef, panelRef])

    useEffect(reposition, [])

    useEffect(() => {
        // Close if we resize the window or click anywhere
        window.addEventListener('resize', reposition)
        window.addEventListener('mouseup', rotateFinish)
        return () => {
            window.removeEventListener('resize', reposition)
            window.removeEventListener('mouseup', rotateFinish)
        }
    }, [rotateFinish, reposition])

    useEffect(() => {
        setLocalHeading(mapHeading(Math.round(convertRadToDeg(heading))))
    }, [heading])

    const hasMore = !!(onDuplicate || onChangeSurface)

    const setRotateMode = () => {
        if (!mode) {
            setMode(MODE_ROTATE)
        }
    }
    const onRotateEnterKey = (event) => {
        const { key } = event
        if (key === 'Enter') {
            const rangeElement = rotateRangeRef && rotateRangeRef.current
            if (rangeElement) {
                rangeElement.focus()
                setMode(MODE_ROTATE)
                rotateStart()
            }
        }
    }
    const onRangeEnterKey = (event) => {
        const { key } = event
        if (key === 'Tab' || key === 'Enter') {
            const rangeElement = rotateRangeRef && rotateRangeRef.current
            if (rangeElement) {
                rangeElement.blur()
                rotateFinish()
            }
            const secondActionButton = document.querySelector(
                // eslint-disable-next-line i18next/no-literal-string
                '.actionPanelButtons button:nth-of-type(1)',
            )
            if (secondActionButton) {
                secondActionButton.focus()
            }
        }
    }

    return (
        <div
            className={classNames('ActionPanel', { openUp, openLeft })}
            style={position}
            ref={panelRef}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
        >
            <Tooltip />
            <div className={classNames('actionPanelButtons', { active: !!mode })}>
                {onRotate && (
                    <button
                        className={classNames({ active: mode === MODE_ROTATE })}
                        onClick={() => setMode(MODE_ROTATE)}
                        onMouseEnter={setRotateMode}
                        onFocus={setRotateMode}
                        onKeyDown={onRotateEnterKey}
                        onBlur={() => {
                            if (!isRotating) {
                                setMode(null)
                            }
                        }}
                        data-tip={t('design.rotate in place')}
                        tabIndex={0}
                        aria-label={t('design.rotate in place')}
                    >
                        <Icon code="rotate" />
                    </button>
                )}
                {onAddToCart && (
                    <button
                        onClick={() => {
                            setMode(null)
                            onAddToCart()
                        }}
                        data-tip={t('design.add to bag')}
                        tabIndex={0}
                        aria-label={t('design.add to bag')}
                    >
                        <Icon code="shoppingBagAdd" />
                    </button>
                )}
                {onDelete && (
                    <button
                        onClick={() => {
                            setMode(null)
                            onDelete()
                        }}
                        data-tip={t('design.remove from design')}
                        tabIndex={0}
                        aria-label={t('design.remove from design')}
                    >
                        <Icon code="trashCan" />
                    </button>
                )}
                {!onChangeSurface && (
                    <FavoriteButtonContainer localItemNumber={localItemNumber} location={TRACK_LOCATION_ACTION_PANEL} />
                )}
                {onChangeSurface && (
                    <button
                        onClick={() => {
                            setMode(null)
                            onChangeSurface()
                        }}
                        data-tip={t('design.change surface')}
                        tabIndex={0}
                        aria-label={t('design.change surface')}
                    >
                        <Icon code="arrowUp" />
                    </button>
                )}
                {hasMore && (
                    <button className={classNames({ active: mode === MODE_MORE })} onClick={() => setMode(MODE_MORE)}>
                        <Icon code="ellipsis" />
                    </button>
                )}
            </div>
            <div className="actionPanelContent">
                {mode === MODE_ROTATE && (
                    <div className="rotateContainer">
                        <Range
                            type="range"
                            min={-180}
                            max={180}
                            step={1}
                            value={localHeading}
                            onChange={rotate}
                            onMouseDown={rotateStart}
                            unit={<>&deg;</>} // eslint-disable-line i18next/no-literal-string
                            onKeyDown={onRangeEnterKey}
                            ref={rotateRangeRef}
                        />
                    </div>
                )}
                {mode === MODE_MORE && (
                    <ul className="moreContainer">
                        {onDuplicate && (
                            <li>
                                <button
                                    onClick={() => {
                                        setMode(null)
                                        onDuplicate()
                                    }}
                                >
                                    <Icon code="copy" />
                                    {t('design.duplicate item')}
                                </button>
                            </li>
                        )}
                        {onChangeSurface && (
                            <li>
                                <FavoriteButtonContainer
                                    localItemNumber={localItemNumber}
                                    location={TRACK_LOCATION_ACTION_PANEL}
                                    text={t('design.add to favorites')}
                                />
                            </li>
                        )}
                    </ul>
                )}
            </div>
        </div>
    )
}

ActionPanel.defaultProps = {
    onAddToCart: null,
    onDelete: null,
    onRotate: null,
    onRotateStart: null,
    onRotateFinish: null,
    onDuplicate: null,
    onChangeSurface: null,
    heading: null,
}

ActionPanel.propTypes = {
    targetRef: shape({}).isRequired,
    onDelete: func,
    onRotateStart: func,
    onRotate: func,
    onRotateFinish: func,
    heading: number,
    onAddToCart: func,
    onDuplicate: func,
    onChangeSurface: func,
    localItemNumber: string.isRequired,
}

export default ActionPanel
